import { BaseSerializer } from '@core/serializers/baseSerializer';

class RestaurantSerializer extends BaseSerializer {
  // Serialize data for API requests (search filters, etc.)
  serialize(data) {
    if (!data) return {};

    if (data.searchQuery || data.filters) {
      return {
        query: data.searchQuery || '',
        cuisine: data.filters?.cuisine || [],
        rating: data.filters?.rating || null,
        priceRange: data.filters?.priceRange || null,
        isOpen: data.filters?.isOpen,
        limit: data.limit || 20,
        offset: data.offset || 0
      };
    }

    return { ...data };
  }

  // Deserialize API responses to app format
  deserialize(raw) {
    const serverRes = raw.data || raw.error;

    const apiSuccess =
      serverRes.success === true || serverRes.statusCode === 200;

    if (!apiSuccess) {
      return {
        statusCode: serverRes.statusCode || raw.status || 500,
        success: false,
        message: serverRes.message || 'Erreur lors de la requête',
        data: null
      };
    }

    const responseData = serverRes.data || serverRes;

    return {
      statusCode: 200,
      success: true,
      message: 'OK',
      data: this.deserializeRestaurant(responseData)
    };
  }

  deserializeDishes(raw) {
    const serverRes = raw.data || raw.error;

    const apiSuccess =
      serverRes.success === true || serverRes.statusCode === 200;
    if (!apiSuccess) {
      return {
        statusCode: serverRes.statusCode || raw.status || 500,
        success: false,
        message: serverRes.message || 'Erreur lors de la requête',
        data: null
      };
    }

    return {
      statusCode: 200,
      success: true,
      message: 'OK',
      data: (serverRes?.data || []).map(formatSingleDish)
    };
  }

  /**
   * Vérifie si le moment présent se situe dans les heures d'ouverture définies pour le restaurant.
   * @param {Object} openingHours - Objet contenant les horaires de chaque jour
   * @returns {boolean}
   */
  isWithinOpeningHours(openingHours) {
    if (!openingHours || typeof openingHours !== 'object') {
      return true; // Si non renseigné, on ne bloque pas par défaut
    }

    const now = new Date();

    // Récupère le nom du jour en anglais ("monday", "tuesday", etc.)
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayKey = days[now.getDay()];

    const todaySchedule = openingHours[currentDayKey];

    // Si le jour n'existe pas ou est marqué comme fermé (`opened: false`)
    if (!todaySchedule || todaySchedule.opened === false) {
      return false;
    }

    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    // Helper pour convertir "9h", "9h30", "15h" en minutes depuis minuit
    const parseTimeToMinutes = (timeStr) => {
      if (!timeStr) return null;
      const clean = timeStr.trim().toLowerCase();
      const parts = clean.split('h');
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      return hours * 60 + minutes;
    };

    // Helper pour vérifier si l'heure actuelle est dans un range de type "9h - 15h"
    const isTimeInSlot = (slotStr) => {
      if (!slotStr || typeof slotStr !== 'string') return false;
      const [startStr, endStr] = slotStr.split('-');
      if (!startStr || !endStr) return false;

      const start = parseTimeToMinutes(startStr);
      const end = parseTimeToMinutes(endStr);

      if (start === null || end === null) return false;

      // Gestion des plages qui dépassent minuit (ex: 22h - 02h)
      if (end < start) {
        return currentMinutes >= start || currentMinutes <= end;
      }

      return currentMinutes >= start && currentMinutes <= end;
    };

    const inLunch = isTimeInSlot(todaySchedule.lunch);
    const inDinner = isTimeInSlot(todaySchedule.dinner);

    return inLunch || inDinner;
  }

  deserializeRestaurant(restaurant) {
    console.log("🚀 ~ RestaurantSerializer ~ deserializeRestaurant ~ restaurant:", restaurant);
    
    const address = typeof restaurant.address === 'string' ? restaurant.address : (restaurant.address || {});
    const openingHours = restaurant.openingHours || {};

    // Condition composée : statut de l'API (isOpen) ET vérification des plages horaires (API en priorité)
    const isManualOpen = restaurant.isOpen !== undefined ? restaurant.isOpen !== false : true;
    const isScheduleOpen =
      restaurant.isWithinOpeningHours !== undefined
        ? restaurant.isWithinOpeningHours !== false
        : this.isWithinOpeningHours(openingHours);
    const calculatedIsOpen = isManualOpen && isScheduleOpen;

    return {
      ...restaurant,
      id: restaurant.id,
      name: restaurant.storeName || restaurant.name,
      storeName: restaurant.storeName || restaurant.name,
      description: restaurant.description || '',
      image:
        restaurant.image ||
        restaurant.coverImage ||
        restaurant.images?.[0] ||
        '',
      images: restaurant.images || [],
      logo: restaurant.logo || '',
      cuisine: restaurant.categories || restaurant.cuisine || [],
      categories: restaurant.categories || restaurant.cuisine || [],
      rating: restaurant.statistics?.rating ?? restaurant.rating ?? 0,
      ratingStars: Math.round(restaurant.statistics?.rating ?? restaurant.ratingStars ?? restaurant.rating ?? 0),
      reviewCount: restaurant.statistics?.ratingCount ?? restaurant.reviewCount ?? 0,
      views: restaurant.statistics?.views ?? restaurant.views ?? 0,
      favorites: restaurant.statistics?.favorites ?? restaurant.favorites ?? 0,
      ordersCount: restaurant.statistics?.ordersCount ?? restaurant.ordersCount ?? 0,
      statistics: restaurant.statistics || {},
      priceRange: restaurant.priceRange || 1,
      lat: restaurant.lat || 0,
      lng: restaurant.lng || 0,
      address: address,
      city: restaurant.city || (typeof address === 'object' ? address.city : ''),
      phone: restaurant.phone || '',
      email: restaurant.email || '',
      facebookLink: restaurant.facebookLink || restaurant.socialLinks?.facebook || '',
      instagramLink: restaurant.instagramLink || restaurant.socialLinks?.instagram || '',
      whatsappNumberHelp: restaurant.whatsappNumberHelp || '',
      
      // Nouveau statut isOpen calculé
      isOpen: calculatedIsOpen,
      isWithinOpeningHours: isScheduleOpen, // Utile si vous souhaitez l'afficher à part dans le UI
      
      openingHours: openingHours,
      drinks: restaurant.drinks || [],
      deliveryTime:
        restaurant.deliveryTime || restaurant.deliverTime || '30-45 min',
      deliverTime:
        restaurant.deliveryTime || restaurant.deliverTime || '30-45 min',
      deliveryFee: restaurant.deliveryFee || 0,
      minOrderAmount: restaurant.minOrderAmount || 0,
      featured: restaurant.featured || false,
      verified: restaurant.verified || false,
      tags: restaurant.tags || [],
      hasPromotion: restaurant.hasPromotion || false,
      promotionSummary: restaurant.promotionSummary || '',
      activePromotions: restaurant.activePromotions || [],
      createdAt: restaurant.createdAt || new Date().toISOString()
    };
  }

  serializeMenu(menuData) {
    const categories = menuData.categories || [];

    return {
      restaurantId: menuData.restaurantId,
      categories: categories.map((category) => ({
        id: category.id,
        name: category.name,
        description: category.description || '',
        items: (category.items || []).map((item) =>
          this.serializeMenuItem(item)
        )
      })),
      popularItems: (menuData.popularItems || []).map((item) =>
        this.serializeMenuItem(item)
      )
    };
  }

  serializeMenuItem(item) {
    return {
      id: item.id,
      name: item.name || item.title,
      title: item.name || item.title,
      description: item.description || '',
      image: item.image || item.principalImageUrl || '',
      principalImageUrl: item.image || item.principalImageUrl || '',
      price: item.price || 0,
      currency: item.currency || 'XOF',
      category: item.category || '',
      categoryId: item.categoryId || '',
      available: item.available !== undefined ? item.available : true,
      preparationTime:
        item.preparationTime || item.cookDurationInMin || '15-20 min',
      cookDurationInMin: item.preparationTime || item.cookDurationInMin || 15,
      options: (item.options || []).map((option) => ({
        id: option.id,
        name: option.name,
        type: option.type || 'single',
        required: option.required || false,
        values: (option.values || []).map((value) => ({
          id: value.id,
          name: value.name,
          price: value.price || 0
        }))
      })),
      addons: (item.addons || []).map((addon) => ({
        id: addon.id,
        name: addon.name,
        price: addon.price || 0
      })),
      accompaniments: (item.accompaniments || []).map((acc) => ({
        id: acc.id || acc._id,
        name: acc.name,
        price: acc.price || 0,
        amount: acc.amount || acc.price || 0,
        description: acc.description || ''
      })),
      tags: item.tags || [],
      spicy: item.spicy || 0,
      vegetarian: item.vegetarian || false,
      popular: item.popular || false,
      promotion: item.promotion || null
    };
  }
}

function formatSingleDish(apiData) {
  if (!apiData) return null;

  return {
    id: apiData.id || apiData._id,
    title: apiData.name || apiData.title,
    name: apiData.name || apiData.title,
    description: apiData.description || '',
    price: apiData.price || 0,
    currency: apiData.currency || 'XOF',

    category: apiData.category,
    categoryId: apiData.categoryId,

    principalImageUrl: apiData.principalImageUrl || apiData.image,
    images: apiData.images || [],

    isPublished: apiData.isPublished !== undefined ? apiData.isPublished : true,
    isFeatured: apiData.isFeatured || false,
    isTopSeller: apiData.isTopSeller || false,

    restaurantId: apiData.restaurantId || apiData.restaurant?.id,
    storeId: apiData.restaurantId || apiData.restaurant?.id,
    storeName:
      apiData.restaurant?.name || apiData.restaurantName || 'Restaurant',
    restaurant: apiData.restaurant
      ? {
          id: apiData.restaurant.id,
          name: apiData.restaurant.name,
          city: apiData.restaurant.city
        }
      : null,

    promotion: apiData.promotion || null,
    options: apiData.options || [],
    accompaniments: (apiData.accompaniments || []).map(acc => ({
      id: acc.id || acc._id,
      name: acc.name,
      price: acc.price || 0,
      amount: acc.amount || acc.price || 0,
      description: acc.description || ''
    })),
    cookDurationInMin: apiData.cookDurationInMin || 15,

    isRestaurantOpen: apiData.restaurant?.isOpen !== false,
    canOrder:
      apiData.isPublished !== false && apiData.restaurant?.isOpen !== false,

    createdAt: apiData.createdAt,
    updatedAt: apiData.updatedAt,

    raw: apiData
  };
}

export const serializer = new RestaurantSerializer();